import { AuthGuard } from "@/components/layout/AuthGuard";
import { PrivateLayout } from "@/components/layout/PrivateLayout";
import { refreshSessionApi } from "@/api/auth.api";
import { useThemeStore } from "@/store/useThemeStore";
import { useAuthStore } from "@/store/useAuthStore";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";

const LoginPage = lazy(() => import("./pages/LoginPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const OrgOnboardingPage = lazy(() => import("./pages/OrgOnboardingPage"));
const SetPasswordPage = lazy(() => import("./pages/SetPasswordPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const OrgSelectPage = lazy(() => import("./pages/OrgSelectPage"));
const CreateOrgPage = lazy(() => import("./pages/CreateOrgPage"));
const InvitationAcceptPage = lazy(() => import("./pages/InvitationAcceptPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const EmployeesActivityPage = lazy(
  () => import("./pages/EmployeesActivityPage"),
);
const EmployeeDetailPage = lazy(() => import("./pages/EmployeeDetailPage"));
const ScreenshotsPage = lazy(() => import("./pages/ScreenshotsPage"));
const AttendancePage = lazy(() => import("./pages/AttendancePage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const LiveMonitorPage = lazy(() => import("./pages/LiveMonitorPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const AdminOverviewPage = lazy(() => import("./pages/admin/AdminOverviewPage"));
const AdminOrganizationsPage = lazy(
  () => import("./pages/admin/AdminOrganizationsPage"),
);
const AdminOrgDetailPage = lazy(
  () => import("./pages/admin/AdminOrgDetailPage"),
);
const AdminSubscriptionsPage = lazy(
  () => import("./pages/admin/AdminSubscriptionsPage"),
);
const AdminPlansPage = lazy(() => import("./pages/admin/AdminPlansPage"));
const AdminSettingsPage = lazy(() => import("./pages/admin/AdminSettingsPage"));
const ApiDocsPage = lazy(() => import("./pages/ApiDocsPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      gcTime: 5 * 60_000,
    },
  },
});

function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-primary border-t-transparent" />
    </div>
  );
}

function ThemeApplier() {
  const resolved = useThemeStore((s) => s.resolved);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", resolved);
    document.documentElement.classList.toggle("dark", resolved === "dark");
  }, [resolved]);

  return null;
}

/**
 * Smart root redirect: authenticated users go to their appropriate home.
 * Unauthenticated → /login.
 */
function RootRedirect() {
  const {
    isAuthenticated,
    hasOrg,
    isPlatformAdmin,
    isCrmMember,
    organizations,
  } = useAuthStore();
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (isPlatformAdmin()) return <Navigate to="/admin" replace />;
  if (isCrmMember()) {
    // CRM members (admin/hr/manager/team-leader) go straight to admin dashboard
    if (!hasOrg()) return <Navigate to="/select-org" replace />;
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (!hasOrg()) {
    // Has orgs but none selected this session → pick one
    if (organizations.length > 0) return <Navigate to="/select-org" replace />;
    // No orgs at all → create first org
    return <Navigate to="/onboarding" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  const resolved = useThemeStore((s) => s.resolved);
  const hydrateSession = useAuthStore((s) => s.hydrateSession);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const [authBootstrapped, setAuthBootstrapped] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function bootstrapAuth() {
      try {
        const session = await refreshSessionApi();
        if (cancelled) {
          return;
        }

        hydrateSession({
          token: session.token,
          user: session.user,
          organizations: session.organizations,
          currentOrg: session.currentOrg
            ? { id: session.currentOrg.id, role: session.currentOrg.role }
            : null,
        });
      } catch {
        if (!cancelled) {
          clearAuth();
        }
      } finally {
        if (!cancelled) {
          setAuthBootstrapped(true);
        }
      }
    }

    void bootstrapAuth();

    return () => {
      cancelled = true;
    };
  }, [clearAuth, hydrateSession]);

  if (!authBootstrapped) {
    return (
      <QueryClientProvider client={queryClient}>
        <ThemeApplier />
        <PageLoader />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeApplier />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route
              path="/signup"
              element={<Navigate to="/onboarding" replace />}
            />
            <Route path="/onboarding" element={<OrgOnboardingPage />} />
            <Route
              path="/onboarding/payment-success"
              element={<PaymentSuccessPage />}
            />
            <Route path="/set-password/:token" element={<SetPasswordPage />} />
            <Route
              path="/select-org"
              element={
                <AuthGuard>
                  <OrgSelectPage />
                </AuthGuard>
              }
            />
            <Route
              path="/create-org"
              element={
                <AuthGuard>
                  <CreateOrgPage />
                </AuthGuard>
              }
            />
            <Route
              path="/auth/invitations/accept/:token"
              element={<InvitationAcceptPage />}
            />
            <Route element={<PrivateLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/employees" element={<EmployeesActivityPage />} />
              <Route path="/employees/:id" element={<EmployeeDetailPage />} />
              <Route path="/screenshots" element={<ScreenshotsPage />} />
              <Route path="/attendance" element={<AttendancePage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/live" element={<LiveMonitorPage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Route>
            <Route path="/docs" element={<ApiDocsPage />} />
            <Route path="/docs/:endpointId" element={<ApiDocsPage />} />

            <Route
              path="/admin"
              element={
                <AuthGuard>
                  <AdminLayout />
                </AuthGuard>
              }
            >
              <Route index element={<AdminOverviewPage />} />
              <Route
                path="organizations"
                element={<AdminOrganizationsPage />}
              />
              <Route
                path="organizations/:orgId"
                element={<AdminOrgDetailPage />}
              />
              <Route
                path="subscriptions"
                element={<AdminSubscriptionsPage />}
              />
              <Route path="plans" element={<AdminPlansPage />} />
              <Route path="settings" element={<AdminSettingsPage />} />
              {/* Tracking pages inside admin layout — these stay within /admin/* */}
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="employees" element={<EmployeesActivityPage />} />
              <Route path="employees/:id" element={<EmployeeDetailPage />} />
              <Route path="screenshots" element={<ScreenshotsPage />} />
              <Route path="attendance" element={<AttendancePage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="live" element={<LiveMonitorPage />} />
            </Route>
            <Route path="*" element={<RootRedirect />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster
        theme={resolved === "dark" ? "dark" : "light"}
        position="bottom-right"
        richColors
        closeButton
      />
    </QueryClientProvider>
  );
}
